import type { ConcentrationTimePoint } from '@bioavail/shared-types';
import { logLinearRegression } from './regression';

export interface LambdaZEstimate {
  lambdaZ: number;
  r2: number;
  pointsUsed: number[];
  aucExtrapolated: number;
  extrapPercent: number;
  cLast: number;
}

const R_SQUARED_MIN = 0.85;

/**
 * Estimate the terminal elimination rate constant λ_z (PSD §4.2.4).
 * Auto-detects the longest terminal run with R² ≥ 0.85, walking backward from
 * the last point. Respects a manual [start, end] index override (F-011).
 *
 * Returns extrapolated AUC = cLast / λ_z and extrapolation %.
 */
export function estimateLambdaZ(
  inputPoints: ConcentrationTimePoint[],
  aucZeroToT: number,
  manualStartIndex?: number,
  manualEndIndex?: number,
): LambdaZEstimate {
  const points = [...inputPoints].sort((a, b) => a.time - b.time);
  const positive = points.filter((p) => p.concentration > 0 && !p.isBlq);

  const useManual =
    manualStartIndex !== undefined && manualEndIndex !== undefined && manualStartIndex >= 0;

  let chosen: ConcentrationTimePoint[];

  if (useManual) {
    const s = Math.max(0, manualStartIndex!);
    const e = Math.min(points.length - 1, manualEndIndex!);
    chosen = points.slice(s, e + 1).filter((p) => p.concentration > 0 && !p.isBlq);
    if (chosen.length < 3) {
      throw new Error('Terminal phase has fewer than 3 positive points; AUC(0-∞) unavailable.');
    }
  } else {
    if (positive.length < 3) {
      throw new Error('Terminal phase has fewer than 3 positive points; AUC(0-∞) unavailable.');
    }

    // Auto-detect: longest terminal run with R² ≥ 0.85, walking backward.
    let best: ConcentrationTimePoint[] | null = null;
    for (let start = positive.length - 3; start >= 0; start--) {
      const candidate = positive.slice(start);
      try {
        const fit = logLinearRegression(
          candidate.map((p) => ({ time: p.time, concentration: p.concentration })),
        );
        if (fit.r2 >= R_SQUARED_MIN && fit.slope < 0) {
          best = candidate;
        } else if (best !== null) {
          // Quality degraded; stop extending backward.
          break;
        }
      } catch {
        break;
      }
    }
    chosen = best ?? positive.slice(-3);
  }

  const fit = logLinearRegression(
    chosen.map((p) => ({ time: p.time, concentration: p.concentration })),
  );

  if (fit.slope >= 0) {
    throw new Error(
      'Terminal slope is non-negative (λ_z ≤ 0); the terminal phase has not been reached.',
    );
  }

  const lambdaZ = -fit.slope;
  const r2 = fit.r2;

  // cLast = last non-BLQ positive concentration in original (sorted) points.
  const lastPositive = [...points].reverse().find((p) => p.concentration > 0 && !p.isBlq);
  const cLast = lastPositive ? lastPositive.concentration : 0;
  const aucExtrapolated = lambdaZ > 0 ? cLast / lambdaZ : 0;
  const aucZeroToInf = aucZeroToT + aucExtrapolated;
  const extrapPercent = aucZeroToInf > 0 ? (aucExtrapolated / aucZeroToInf) * 100 : 0;

  const pointsUsed = chosen.map((c) => points.indexOf(c));

  return { lambdaZ, r2, pointsUsed, aucExtrapolated, extrapPercent, cLast };
}

/**
 * Recommend how far to extend sampling so that AUC extrapolation drops to (or
 * below) `target` (default 20%) — F-069.
 *
 * Solves for the additional sampling time `dt` such that
 * `extrap_new / (aucZeroToT + extrap_new) <= target`, where
 * `extrap_new = cLast * exp(-lambdaZ * dt) / lambdaZ` (the last concentration
 * decays by `exp(-lambdaZ·dt)` over the extension). Bisects over
 * `dt ∈ [0, 10·ln(2)/lambdaZ]` with 0.1 h precision.
 */
export function recommendSamplingExtension(input: {
  lambdaZ: number;
  cLast: number;
  aucZeroToT: number;
  target?: number;
}): { additionalHours: number; additionalHalfLives: number } {
  const { lambdaZ, cLast, aucZeroToT } = input;
  const target = input.target ?? 0.2;
  if (lambdaZ <= 0 || cLast <= 0 || aucZeroToT <= 0) {
    return { additionalHours: 0, additionalHalfLives: 0 };
  }

  const extrapAt = (dt: number): number =>
    (cLast * Math.exp(-lambdaZ * dt)) / lambdaZ;

  const ratioAt = (dt: number): number => {
    const e = extrapAt(dt);
    return e / (aucZeroToT + e);
  };

  // Already at or below target — no extension required.
  if (ratioAt(0) <= target) {
    return { additionalHours: 0, additionalHalfLives: 0 };
  }

  const halfLife = Math.LN2 / lambdaZ;
  const lo = 0;
  const hi = 10 * halfLife;

  // If still above target at the upper bound, return the bound.
  if (ratioAt(hi) > target) {
    return { additionalHours: hi, additionalHalfLives: hi / halfLife };
  }

  // Bisection (f is monotonically decreasing in dt).
  let a = lo;
  let b = hi;
  while (b - a > 0.1) {
    const mid = (a + b) / 2;
    if (ratioAt(mid) <= target) {
      b = mid;
    } else {
      a = mid;
    }
  }

  const additionalHours = (a + b) / 2;
  return { additionalHours, additionalHalfLives: additionalHours / halfLife };
}
